import { ChevronLeft, ChevronRight, RestartAlt } from "@mui/icons-material";
import { Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { Dispatch, SetStateAction } from "react";

import { useSessionContext } from "../../contexts/SessionContext";
import { DateFilterType } from "../../utils/interfaces";

interface IProps {
  dateFilter: Dayjs;
  setDateFilter: Dispatch<SetStateAction<Dayjs>>;
  filterType: DateFilterType;
  setFilterType: Dispatch<SetStateAction<DateFilterType>>;
}

export default function DateFilter({
  dateFilter,
  setDateFilter,
  filterType,
  setFilterType,
}: IProps) {
  const { access } = useSessionContext();
  // Formata a data baseado no tipo de filtro
  const formatType =
    filterType === "day"
      ? "DD/MM/YYYY"
      : filterType === "week"
      ? "DD/MM/YYYY" // Para semana, mostra a data inicial
      : "MMMM YYYY";

  function subtractDate(filter?: DateFilterType) {
    return dayjs(dateFilter).subtract(1, filter || filterType);
  }

  function addDate(filter?: DateFilterType) {
    return dayjs(dateFilter).add(1, filter || filterType);
  }

  function handleChangeDate(operation: "subtract" | "add") {
    let auxFilterType = filterType;
    if (access.type !== "professional") {
      setFilterType("month");
      filterType = "month";
    }
    switch (operation) {
      case "subtract":
        setDateFilter(subtractDate(auxFilterType));
        return;
      case "add":
        setDateFilter(addDate(auxFilterType));
        return;
    }
  }

  /** Retorna ao período atual (mês para paciente; conforme filterType no profissional) */
  function handleResetToCurrentPeriod() {
    const now = dayjs();
    setDateFilter(now);
    if (access.type !== "professional") {
      setFilterType("month");
    }
  }

  const resetUnit =
    filterType === "day" ? "day" : filterType === "week" ? "week" : "month";
  const isCurrentPeriod = dayjs(dateFilter).isSame(dayjs(), resetUnit);

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Button
        variant="text"
        disabled={subtractDate().isBefore(dayjs(), filterType)}
        sx={{
          border: "1px solid",
          borderColor: "primary400.main",
          minWidth: "24px",
          height: "24px",
        }}
        onClick={() => handleChangeDate("subtract")}
      >
        <ChevronLeft />
      </Button>
      <Typography
        variant="subtitle2"
        fontWeight={600}
        sx={{ color: "neutral700.main", textTransform: "capitalize" }}
      >
        {dayjs(dateFilter).format(formatType)}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {!isCurrentPeriod && (
          <Tooltip
            title={
              resetUnit === "day"
                ? "Voltar para hoje"
                : resetUnit === "week"
                  ? "Voltar para semana atual"
                  : "Voltar ao mês atual"
            }
            arrow
          >
            <IconButton
              size="small"
              aria-label="Voltar ao período atual"
              onClick={handleResetToCurrentPeriod}
              sx={{
                border: "1px solid",
                borderColor: "primary400.main",
                width: 24,
                height: 24,
                p: 0,
              }}
            >
              <RestartAlt sx={{ fontSize: 16, color: "primary.main" }} />
            </IconButton>
          </Tooltip>
        )}
        <Button
        variant="text"
        sx={{
          border: "1px solid",
          borderColor: "primary400.main",
          minWidth: "24px",
          height: "24px",
        }}
        onClick={() => handleChangeDate("add")}
      >
        <ChevronRight />
      </Button>
      </Stack>
    </Stack>
  );
}
